import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import crmService from '../../../services/crmService';
import jobApplicationService from '../../../services/jobApplicationService';
import SendToClientModal from './SendToClientModal';

// Get API base URL from environment or use relative path
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';

// Helper function to parse skills (handle both JSON string and array)
const parseSkills = (skills) => {
  if (!skills) return [];
  if (Array.isArray(skills)) return skills;
  if (typeof skills === 'string') {
    try {
      const parsed = JSON.parse(skills);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If not valid JSON, try splitting by comma
      return skills.split(',').map(s => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const backendRoot = API_BASE_URL.replace(/\/api\/?$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${backendRoot}${cleanPath}`;
};

const LeadDetailModal = ({ lead, onClose, onLeadUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [movingToATS, setMovingToATS] = useState(false);
  const [actionError, setActionError] = useState('');

  // Parse skills safely
  const leadSkills = parseSkills(lead?.skills);

  const [formData, setFormData] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    skills: leadSkills.join(', ') || '',
    experience_years: lead?.experience_years || 0,
    skill_level: lead?.skill_level || 'entry',
    status: lead?.status || 'new',
    notes: lead?.notes || '',
    // New salary and availability fields
    current_salary: lead?.current_salary || '',
    expected_salary: lead?.expected_salary || '',
    notice_period: lead?.notice_period || '',
    location_preferred: lead?.location_preferred || '',
    previous_company: lead?.previous_company || '',
    previous_company_details: lead?.previous_company_details || '',
    resume_url: lead?.resume_url || ''
  });
  
  const [resumeFile, setResumeFile] = useState(null);
  const hasValidCall = lead?.lead_calls?.some(call => call.is_valid_lead === true || call.is_valid_lead === 1 || call.is_valid_lead === '1');
  const canMoveToATS = ['validated', 'shortlisted', 'sent_to_client'].includes(lead?.status) || hasValidCall;
  const isMovedToATS = lead?.status === 'moved_to_ats';

  // Load call history
  useEffect(() => {
    const loadCallHistory = async () => {
      if (lead?.id) {
        try {
          const result = await crmService.getLeadCallHistory(lead.id);
          if (result?.success) {
            setCallHistory(result.data || []);
          } else {
            console.error('Failed to load call history:', result?.error);
            setCallHistory([]);
          }
        } catch (error) {
          console.error('Error loading call history:', error);
          setCallHistory([]);
        }
      } else {
        setCallHistory([]);
      }
    };

    // Load when lead changes or when switching to calls tab
    if (activeTab === 'calls' || lead?.id) {
      loadCallHistory();
    }
  }, [lead?.id, activeTab]);

  // Load submission history
  useEffect(() => {
    const loadSubmissionHistory = async () => {
      if (lead?.id) {
        try {
          setLoadingSubmissions(true);
          const result = await crmService.getLeadSubmissions(lead.id);
          if (result?.success) {
            setSubmissionHistory(result.data || []);
          } else {
            console.error('Failed to load submission history:', result?.error);
            setSubmissionHistory([]);
          }
        } catch (error) {
          console.error('Error loading submission history:', error);
          setSubmissionHistory([]);
        } finally {
          setLoadingSubmissions(false);
        }
      } else {
        setSubmissionHistory([]);
      }
    };

    // Load when lead changes or when switching to submissions tab
    if (activeTab === 'submissions' || lead?.id) {
      loadSubmissionHistory();
    }
  }, [lead?.id, activeTab]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // If there's a new resume file, upload it first
      let resumeUrl = formData.resume_url;
      if (resumeFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', resumeFile);
        formDataUpload.append('type', 'resume');
        
        try {
          const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formDataUpload,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('erp_auth_token')}`
            }
          });
          
          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            resumeUrl = uploadResult.url || uploadResult.data?.url;
            console.log('Resume uploaded successfully:', resumeUrl);
          } else {
            console.error('Resume upload failed');
          }
        } catch (uploadError) {
          console.error('Resume upload error:', uploadError);
        }
      }

      const updateData = {
        ...formData,
        resume_url: resumeUrl,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : []
      };

      const result = await crmService.updateLead(lead.id, updateData);

      if (result?.success) {
        onLeadUpdated?.(result.data);
        setEditMode(false);
        setResumeFile(null); // Clear the file after successful save
      }
    } catch (error) {
      console.log('Lead update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToATS = async () => {
    if (!lead?.id || movingToATS) return;
    const confirmed = window.confirm('Move this lead to Applicant Tracking System?');
    if (!confirmed) return;
    setMovingToATS(true);
    setActionError('');
    try {
      const result = await jobApplicationService.importFromLeads({ lead_ids: [lead.id] });
      if (result?.success && result.data?.successful > 0) {
        const updatedLead = {
          ...lead,
          status: 'moved_to_ats',
          notes: (lead?.notes || '') + `\n[Imported to Applicant Tracking System on ${new Date().toLocaleString()}]`
        };
        onLeadUpdated?.(updatedLead);
      } else {
        const errorMessage = result.data?.errors?.[0] || result?.error || 'Failed to move lead to ATS (possibly duplicate email)';
        setActionError(errorMessage);
      }
    } catch (error) {
      setActionError(error?.message || 'Failed to move lead to ATS');
    } finally {
      setMovingToATS(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      contacted: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      shortlisted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      sent_to_client: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
      placed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      moved_to_ats: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return status?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || '';
  };

  const formatCallOutcome = (outcome) => {
    return outcome?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {lead?.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{lead?.name}</h2>
              <p className="text-sm text-muted-foreground">{lead?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditMode(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2"
              >
                <Icon name="Edit" size={16} />
                Edit
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        {(lead?.status === 'shortlisted' || canMoveToATS || isMovedToATS) && (
          <div className="px-6 py-3 border-b border-border bg-muted/30">
            <div className="flex flex-wrap items-center gap-2">
              {lead?.status === 'shortlisted' && (
                <Button
                  variant="default"
                  onClick={() => setShowSendToClientModal(true)}
                  className="flex items-center gap-2"
                >
                  <Icon name="Send" size={16} />
                  Send to Client
                </Button>
              )}
              {(canMoveToATS || isMovedToATS) && (
                <Button
                  variant={isMovedToATS ? 'outline' : 'default'}
                  onClick={handleMoveToATS}
                  disabled={movingToATS || isMovedToATS}
                  className="flex items-center gap-2"
                >
                  <Icon name="ArrowRight" size={16} />
                  {movingToATS ? 'Moving to ATS...' : isMovedToATS ? 'Moved to ATS' : 'Move to ATS'}
                </Button>
              )}
              {actionError && (
                <span className="text-sm text-destructive">{actionError}</span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('calls')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'calls' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              Call History ({callHistory?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'submissions' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              Client Submissions ({submissionHistory?.length || 0})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead?.status)}`}>
                  {formatStatus(lead?.status)}
                </span>
                <div className="text-sm text-muted-foreground">
                  Created: {lead?.created_at ? new Date(lead.created_at).toLocaleString() : 'N/A'}
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  {editMode ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter full name"
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2">{lead?.name || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email"
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2">{lead?.email || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone
                  </label>
                  {editMode ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2">{lead?.phone || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Experience (Years)
                  </label>
                  {editMode ? (
                    <Input
                      type="number"
                      value={formData.experience_years}
                      onChange={(e) => handleInputChange('experience_years', parseInt(e.target.value) || 0)}
                      placeholder="Enter years of experience"
                      min="0"
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2">{lead?.experience_years || 0} years</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Skill Level
                  </label>
                  {editMode ? (
                    <Select
                      value={formData.skill_level}
                      onValueChange={(value) => handleInputChange('skill_level', value)}
                      options={[
                        { value: 'entry', label: 'Entry' },
                        { value: 'intermediate', label: 'Intermediate' },
                        { value: 'senior', label: 'Senior' },
                        { value: 'expert', label: 'Expert' }
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2 capitalize">{lead?.skill_level || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  {editMode ? (
                    <Select
                      value={formData.status}
                      onValueChange={(value) => handleInputChange('status', value)}
                      options={[
                        { value: 'new', label: 'New' },
                        { value: 'contacted', label: 'Contacted' },
                        { value: 'validated', label: 'Validated' },
                        { value: 'shortlisted', label: 'Shortlisted' },
                        { value: 'placed', label: 'Placed' },
                        { value: 'rejected', label: 'Rejected' },
                        { value: 'moved_to_ats', label: 'Moved to ATS' }
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-foreground py-2">{formatStatus(lead?.status)}</p>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Skills
                </label>
                {editMode ? (
                  <Input
                    value={formData.skills}
                    onChange={(e) => handleInputChange('skills', e.target.value)}
                    placeholder="Enter skills separated by commas"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2 py-2">
                    {(() => {
                      const displaySkills = parseSkills(lead?.skills);
                      return displaySkills.length > 0 ? (
                        displaySkills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                          >
                            {skill}
                          </span>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No skills listed</p>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Salary & Availability Information */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Salary & Availability</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Current Salary
                    </label>
                    {editMode ? (
                      <Input
                        value={formData.current_salary}
                        onChange={(e) => handleInputChange('current_salary', e.target.value)}
                        placeholder="e.g., ₹5,00,000 per annum"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2">{lead?.current_salary || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Expected Salary
                    </label>
                    {editMode ? (
                      <Input
                        value={formData.expected_salary}
                        onChange={(e) => handleInputChange('expected_salary', e.target.value)}
                        placeholder="e.g., ₹7,00,000 per annum"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2">{lead?.expected_salary || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Notice Period
                    </label>
                    {editMode ? (
                      <Select
                        value={formData.notice_period}
                        onValueChange={(value) => handleInputChange('notice_period', value)}
                        options={[
                          { value: 'immediate', label: 'Immediate' },
                          { value: '7_days', label: '7 Days' },
                          { value: '15_days', label: '15 Days' },
                          { value: '30_days', label: '30 Days' },
                          { value: '45_days', label: '45 Days' },
                          { value: '60_days', label: '60 Days' },
                          { value: '90_days', label: '90 Days' },
                          { value: 'negotiable', label: 'Negotiable' }
                        ]}
                        placeholder="Select notice period"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2">{lead?.notice_period || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Location Preferred
                    </label>
                    {editMode ? (
                      <Input
                        value={formData.location_preferred}
                        onChange={(e) => handleInputChange('location_preferred', e.target.value)}
                        placeholder="e.g., Bangalore, Hyderabad, Remote"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2">{lead?.location_preferred || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Previous Company Details */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Previous Company Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Previous Company Name
                    </label>
                    {editMode ? (
                      <Input
                        value={formData.previous_company}
                        onChange={(e) => handleInputChange('previous_company', e.target.value)}
                        placeholder="Enter previous company name"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2">{lead?.previous_company || 'N/A'}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Previous Company Details
                    </label>
                    {editMode ? (
                      <textarea
                        value={formData.previous_company_details}
                        onChange={(e) => handleInputChange('previous_company_details', e.target.value)}
                        placeholder="Enter details about previous employment"
                        rows={3}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      />
                    ) : (
                      <p className="text-sm text-foreground py-2 whitespace-pre-wrap">
                        {lead?.previous_company_details || 'N/A'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Resume */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Resume</h3>
                {editMode ? (
                  <div className="p-4 bg-muted/30 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground mb-3">
                      {resumeFile 
                        ? `Selected: ${resumeFile.name}` 
                        : lead?.resume_url 
                          ? 'Current resume will be kept' 
                          : 'No resume uploaded'}
                    </p>
                    <input
                      type="file"
                      id="resume-upload-edit"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => setResumeFile(e.target.files[0])}
                      className="hidden"
                    />
                    <label htmlFor="resume-upload-edit" className="cursor-pointer inline-flex items-center px-4 py-2 border border-border rounded-md bg-background text-foreground hover:bg-muted">
                      <Icon name="Upload" size={16} className="mr-2" />
                      {resumeFile ? 'Change File' : 'Upload Resume'}
                    </label>
                  </div>
                ) : (
                  lead?.resume_url ? (
                    <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg border border-border">
                      <Icon name="FileText" size={24} className="text-primary" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Uploaded Resume</p>
                        <a 
                          href={getFileUrl(lead?.resume_url)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          View Resume
                        </a>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(getFileUrl(lead?.resume_url), '_blank')}
                      >
                        <Icon name="Download" size={16} className="mr-2" />
                        Download
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No resume uploaded</p>
                  )
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notes
                </label>
                {editMode ? (
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Add notes about this lead"
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  />
                ) : (
                  <p className="text-sm text-foreground py-2 whitespace-pre-wrap">
                    {lead?.notes || 'No notes available'}
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'calls' && (
            <div className="space-y-4">
              {callHistory?.length > 0 ? (
                callHistory.map((call, index) => (
                  <div key={call.id || index} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">
                            {call?.telecaller_name || call?.telecaller?.full_name || 'Unknown Telecaller'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {call?.call_date ? new Date(call.call_date).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${call?.outcome === 'answered' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
                          }`}>
                          {formatCallOutcome(call?.outcome)}
                        </span>
                        {call?.call_duration > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {Math.floor(call.call_duration / 60)}:{(call.call_duration % 60).toString().padStart(2, '0')}
                          </p>
                        )}
                      </div>
                    </div>

                    {call?.notes && (
                      <p className="text-sm text-foreground mt-2">{call.notes}</p>
                    )}

                    {call?.is_valid_lead !== null && call?.is_valid_lead !== undefined && (
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${call.is_valid_lead
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                          }`}>
                          {call.is_valid_lead ? 'Valid Lead' : 'Invalid Lead'}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Icon name="Phone" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No call history</h3>
                  <p className="text-muted-foreground">
                    This lead has not been contacted yet.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'submissions' && (
            <div className="space-y-4">
              {loadingSubmissions ? (
                <div className="text-center py-8">
                  <Icon name="Loader" size={48} className="text-muted-foreground mx-auto mb-4 animate-spin" />
                  <p className="text-muted-foreground">Loading submission history...</p>
                </div>
              ) : submissionHistory?.length > 0 ? (
                submissionHistory.map((submission, index) => (
                  <div key={submission.id || index} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Icon name="Briefcase" size={20} className="text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">
                              {submission.position_title || submission.requirement_title}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {submission.company_name}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Submitted:</span>
                            <p className="font-medium">
                              {submission.submitted_date ? new Date(submission.submitted_date).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Response:</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-1 ${submission.client_response === 'accepted' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                submission.client_response === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                  submission.client_response === 'under_review' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                                    'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                              }`}>
                              {submission.client_response === 'accepted' ? 'Accepted' :
                                submission.client_response === 'rejected' ? 'Rejected' :
                                  submission.client_response === 'under_review' ? 'Under Review' :
                                    'Pending'}
                            </span>
                          </div>
                          {submission.response_date && (
                            <div>
                              <span className="text-muted-foreground">Response Date:</span>
                              <p className="font-medium">
                                {new Date(submission.response_date).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                        {submission.submitted_by_name && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Submitted by: {submission.submitted_by_name}
                          </p>
                        )}
                        {submission.client_notes && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Client Notes:</p>
                            <p className="text-sm text-foreground">{submission.client_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Icon name="Send" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No submissions</h3>
                  <p className="text-muted-foreground">
                    This lead has not been sent to any client requirements yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Send to Client Modal */}
      <SendToClientModal
        isOpen={showSendToClientModal}
        onClose={() => setShowSendToClientModal(false)}
        lead={lead}
        onSent={(updatedLead) => {
          setShowSendToClientModal(false);
          onLeadUpdated?.(updatedLead);
        }}
      />
    </div>
  );
};

export default LeadDetailModal;